import type { DeviceInfo } from '../types';

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Check if running in browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Check if Do Not Track is enabled
 */
export function isDoNotTrackEnabled(): boolean {
  if (!isBrowser()) return false;
  
  const { doNotTrack, navigator } = window as any;
  const dnt = doNotTrack || navigator.doNotTrack || navigator.msDoNotTrack;
  
  return dnt === '1' || dnt === 'yes';
}

/**
 * Parse user agent to extract device information
 */
export function parseUserAgent(userAgent: string): DeviceInfo {
  const ua = userAgent.toLowerCase();
  
  // Detect device type
  let type: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    type = 'tablet';
  } else if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
    type = 'mobile';
  }
  
  // Detect OS
  let os = 'Unknown';
  if (/windows nt 10/i.test(ua)) os = 'Windows 10';
  else if (/windows nt 6.3/i.test(ua)) os = 'Windows 8.1';
  else if (/windows nt 6.2/i.test(ua)) os = 'Windows 8';
  else if (/windows nt 6.1/i.test(ua)) os = 'Windows 7';
  else if (/windows/i.test(ua)) os = 'Windows';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS';
  else if (/mac os x/i.test(ua)) os = 'macOS';
  else if (/linux/i.test(ua)) os = 'Linux';
  else if (/chromeos/i.test(ua)) os = 'Chrome OS';
  
  // Detect browser
  let browser = 'Unknown';
  let browserVersion = '';
  
  if (/edg\//i.test(ua)) {
    browser = 'Edge';
    const match = ua.match(/edg\/(\d+(\.\d+)*)/);
    if (match) browserVersion = match[1];
  } else if (/chrome|crios/i.test(ua) && !/edg/i.test(ua)) {
    browser = 'Chrome';
    const match = ua.match(/chrome\/(\d+(\.\d+)*)/);
    if (match) browserVersion = match[1];
  } else if (/firefox|fxios/i.test(ua)) {
    browser = 'Firefox';
    const match = ua.match(/firefox\/(\d+(\.\d+)*)/);
    if (match) browserVersion = match[1];
  } else if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) {
    browser = 'Safari';
    const match = ua.match(/version\/(\d+(\.\d+)*)/);
    if (match) browserVersion = match[1];
  } else if (/opera|opr/i.test(ua)) {
    browser = 'Opera';
    const match = ua.match(/(?:opera|opr)\/(\d+(\.\d+)*)/);
    if (match) browserVersion = match[1];
  }
  
  return { type, os, browser, browserVersion };
}

/**
 * Get viewport dimensions
 */
export function getViewport(): string {
  if (!isBrowser()) return '';
  return `${window.innerWidth}x${window.innerHeight}`;
}

/**
 * Get screen resolution
 */
export function getScreenResolution(): string {
  if (!isBrowser()) return '';
  return `${window.screen.width}x${window.screen.height}`;
}

/**
 * Get referrer source
 */
export function getReferrerSource(referrer: string): string {
  if (!referrer || referrer === '') return 'direct';
  
  try {
    const url = new URL(referrer);
    const hostname = url.hostname.replace('www.', '');
    
    // Common social media platforms
    if (hostname.includes('facebook.com') || hostname.includes('fb.com')) return 'facebook';
    if (hostname.includes('twitter.com') || hostname.includes('t.co')) return 'twitter';
    if (hostname.includes('linkedin.com')) return 'linkedin';
    if (hostname.includes('instagram.com')) return 'instagram';
    if (hostname.includes('youtube.com')) return 'youtube';
    if (hostname.includes('reddit.com')) return 'reddit';
    
    // Search engines
    if (hostname.includes('google.')) return 'google';
    if (hostname.includes('bing.com')) return 'bing';
    if (hostname.includes('yahoo.')) return 'yahoo';
    if (hostname.includes('duckduckgo.com')) return 'duckduckgo';
    if (hostname.includes('baidu.com')) return 'baidu';
    
    return hostname;
  } catch {
    return 'invalid';
  }
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Safe JSON parse
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
} 